import { createFileRoute, Link } from "@tanstack/react-router";
import { WISDOM_CATEGORIES } from "@/lib/content";
import { TERRITORIES } from "@/lib/taxonomy";
import { getWisdomIndex } from "@/lib/wisdom.functions";
import { ARTICLE_KIND_LABELS, type PublicWisdomPost } from "@/lib/wisdom-kinds";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import {
  PORTRAIT_ALT,
  PORTRAIT_URL,
  breadcrumbs,
  itemList,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  seoMeta,
} from "@/lib/schema";

const TITLE = "Wisdom by Buddha KA | Essays on Attention, Perception & Inner Infrastructure";
const DESCRIPTION =
  "Essays and mirror thoughts by Buddha KA — first-party writing on attention, perception, sacred business, and the architecture of interior freedom, from the KAfrequency canon.";

export const Route = createFileRoute("/wisdom/")({
  // Published rows only — the database is the source of truth for the room.
  loader: async (): Promise<{ posts: PublicWisdomPost[] }> => ({
    posts: await getWisdomIndex().catch(() => [] as PublicWisdomPost[]),
  }),
  component: WisdomArchive,
  head: ({ loaderData }) => ({
    ...seoMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: "/wisdom",
      image: PORTRAIT_URL,
      imageAlt: PORTRAIT_ALT,
    }),
    links: [
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Wisdom — Essays by Buddha KA",
        href: `${SITE_URL}/wisdom/feed.xml`,
      },
    ],
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: `${SITE_URL}/wisdom#reading-room`,
          url: `${SITE_URL}/wisdom`,
          name: "Wisdom — Essays by Buddha KA",
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: {
            author: personRef(),
            publisher: orgRef(),
            mainEntity: itemList(
              (loaderData?.posts ?? []).map((w) => ({ path: `/wisdom/${w.slug}`, name: w.title })),
            ),
          },
        }),
      ),
      ldScript(breadcrumbs([["Wisdom", "/wisdom"]])),
    ],
  }),
});

const START_HERE_SLUG = "who-is-buddha-ka";

function WisdomArchive() {
  const { posts } = Route.useLoaderData();
  const startHere = posts.find((w: PublicWisdomPost) => w.slug === START_HERE_SLUG) ?? null;
  const rest = posts.filter((w: PublicWisdomPost) => w.slug !== START_HERE_SLUG);

  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-40" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">The Reading Room</p>
          <h1 className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl">Wisdom</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Essays and mirror thoughts by Buddha KA on attention, perception, sacred business, and the
            architecture of interior freedom. Each writing carries companion work in the{" "}
            <Link to="/music" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Buddha KA music catalog</Link>,{" "}
            <Link to="/books" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">the books</Link>, and{" "}
            <Link to="/activations" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">the daily activations</Link>.
          </p>
        </div>
      </section>

      <section aria-labelledby="territories" className="mx-auto max-w-7xl px-4 pt-16 sm:px-6">
        <h2 id="territories" className="text-xs uppercase tracking-[0.3em] text-gold">
          The six territories
        </h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Every writing belongs to a territory. Enter through the one that matches the question you
          arrived with.
        </p>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TERRITORIES.map((t) => (
            <li key={t.slug} className="rounded-2xl border hairline bg-charcoal/50 p-6 transition-colors hover:border-gold">
              <Link
                to="/topics/$slug"
                params={{ slug: t.slug }}
                className="font-serif text-2xl text-ivory hover:text-gold-luminous"
              >
                {t.name}
              </Link>
              <p className="mt-2 text-sm text-muted-foreground">{t.definition}</p>
            </li>
          ))}
        </ul>
      </section>

      {startHere && (
        <section aria-labelledby="start-here" className="mx-auto mt-16 max-w-7xl px-4 sm:px-6">
          <div className="rounded-2xl border border-gold/40 bg-charcoal p-8 shadow-halo">
            <p id="start-here" className="text-xs uppercase tracking-[0.3em] text-gold">
              Start here
            </p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl">
              <Link to="/wisdom/$slug" params={{ slug: startHere.slug }} className="hover:text-gold-luminous">
                {startHere.title}
              </Link>
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">{startHere.excerpt}</p>
            <Link
              to="/wisdom/$slug"
              params={{ slug: startHere.slug }}
              className="mt-6 inline-flex rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
            >
              Read the canonical introduction
            </Link>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-10 flex flex-wrap gap-2">
          <span className="mr-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">Categories:</span>
          {WISDOM_CATEGORIES.map((c) => (
            <span key={c} className="rounded-full border hairline px-3 py-1 text-xs text-muted-foreground">{c}</span>
          ))}
        </div>

        {posts.length === 0 ? (
          <p className="border-t hairline pt-8 text-muted-foreground">
            New writings are being prepared. In the meantime, sit with{" "}
            <Link to="/activations" className="text-gold underline decoration-gold/40 underline-offset-4">today&rsquo;s activation</Link>.
          </p>
        ) : (
          <div className="grid gap-10 md:grid-cols-2">
            {rest.map((w: PublicWisdomPost) => (
              <article key={w.slug} className="group border-t hairline pt-8">
                <p className="text-xs uppercase tracking-[0.25em] text-gold">
                  {ARTICLE_KIND_LABELS[w.articleKind]} · {w.category}
                  {w.readingTime ? ` · ${w.readingTime}` : ""}
                </p>
                <h2 className="mt-3 font-serif text-3xl group-hover:text-gold-luminous">
                  <Link to="/wisdom/$slug" params={{ slug: w.slug }} className="hover:text-gold-luminous">
                    {w.title}
                  </Link>
                </h2>
                <p className="mt-3 text-muted-foreground">{w.excerpt}</p>
                <Link
                  to="/wisdom/$slug"
                  params={{ slug: w.slug }}
                  className="mt-4 inline-flex text-sm text-gold"
                  aria-label={`Read ${w.title} by Buddha KA`}
                >
                  Read {w.title} →
                </Link>
              </article>
            ))}
          </div>
        )}


        <nav aria-label="Related sections" className="mt-20 border-t hairline pt-10">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Continue through the canon</p>
          <ul className="mt-4 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
            <li><Link to="/music" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Buddha KA albums, EPs & singles</Link></li>
            <li><Link to="/books" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Books & written systems by Buddha KA</Link></li>
            <li><Link to="/activations" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Daily activations from KAfrequency</Link></li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            Canonical archive: <a href={absoluteUrl("/wisdom")} className="text-gold">{absoluteUrl("/wisdom")}</a>
          </p>
        </nav>
      </section>
    </>
  );
}
